import type { ChatEvent, ProposedAction, TextDeltaData, ToolCallData, ErrorData } from '../types';

import { authHeaders, getAiServiceBaseUrl } from './service-config';

export interface StreamChatArgs {
  sessionId: string;
  message: string;
  // Only the estimate id is sent; the AI service fetches (and caches) the full
  // estimate from the backend.
  estimateId?: string;
  signal?: AbortSignal;
}

export interface StreamChatHandlers {
  onTextDelta: (text: string) => void;
  onProposedAction: (action: ProposedAction) => void;
  onToolCall: (call: { name: string; args: Record<string, unknown> }) => void;
  onError: (error: string) => void;
  // `ok` is true only for a turn that reached a terminal `done` event with no errors.
  // A stream that just stops -- proxy truncation, an HTTP/2 reset the fetch API
  // surfaces as a clean end, a server bug -- is not a success: reporting one would
  // refetch the estimate and tell the user an apply landed when it may not have.
  onDone: (ok: boolean) => void;
}

export async function streamChat(
  { sessionId, message, estimateId, signal }: StreamChatArgs,
  handlers: StreamChatHandlers
): Promise<void> {
  const { onTextDelta, onProposedAction, onToolCall, onDone } = handlers;

  let failed = false;
  const onError = (error: string) => {
    failed = true;
    handlers.onError(error);
  };

  const res = await fetch(`${getAiServiceBaseUrl()}/chat`, {
    method: 'POST',
    headers: authHeaders(),
    signal,
    body: JSON.stringify({
      sessionId,
      message,
      ...(estimateId ? { estimateId } : {}),
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    onError(`HTTP ${res.status}: ${text}`);
    onDone(false);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    onError('No response body');
    onDone(false);
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let sawDone = false;

  // Returns true once a `done` event has been seen, so the caller stops reading.
  const dispatch = (block: string): boolean => {
    for (const line of block.split(/\r\n|\n|\r/)) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) {
        continue;
      }

      // The space after `data:` is optional per the SSE spec.
      const jsonStr = trimmed.slice(5).trimStart();
      let event: ChatEvent;
      try {
        event = JSON.parse(jsonStr);
      } catch {
        // A malformed event must not be silently ignored: skipping it here would let a
        // later, unrelated `done` event report success for a turn that actually broke.
        onError('Malformed event from AI service');
        continue;
      }

      switch (event.type) {
        case 'text_delta':
          onTextDelta((event.data as TextDeltaData).text);
          break;
        case 'proposed_action':
          onProposedAction(event.data as ProposedAction);
          break;
        case 'tool_call':
          onToolCall(event.data as ToolCallData);
          break;
        case 'error':
          onError((event.data as ErrorData).error);
          break;
        case 'done':
          sawDone = true;
          return true;
      }
    }
    return false;
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // Flush the decoder (a multi-byte character may straddle the last chunk) and
        // parse whatever is left: a final event need not be followed by a blank line,
        // and dropping it silently would turn a trailing `error` into a fake success.
        buffer += decoder.decode();
        if (buffer.trim()) {
          dispatch(buffer);
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Events are separated by a blank line, which may use either line ending.
      const parts = buffer.split(/\r\n\r\n|\n\n|\r\r/);
      buffer = parts.pop() || '';

      let stop = false;
      for (const part of parts) {
        if (dispatch(part)) {
          stop = true;
          break;
        }
      }
      if (stop) {
        break;
      }
    }
  } finally {
    // cancel() closes the connection; releaseLock() alone leaves the body open.
    try {
      await reader.cancel();
    } catch {
      // Already gone.
    }
  }

  onDone(sawDone && !failed);
}
